<template>
  <div :class="$style.base">
    <BaseInput
      v-model="amount"
      :label="$t('global.amount')"
      size="l"
      type="currency"
      v-test="'register-payment-amount'"
    />
    <div>
      <div
        v-if="fetchingPaymentMethods"
        :class="$style.loading"
      >
        <BaseSpinner inline />
      </div>
      <Flex
        direction="column"
        gap="1"
        v-else
      >
        <Flex
          :class="$style.header"
          justify="between"
        >
          <BaseHeading size="xs">
            {{ $t('reports.totals.payment_method') }}
          </BaseHeading>
          <BaseText
            v-if="user?.admin"
            iconBefore="edit"
            @click="showEditPaymentMethods = true"
            v-test="'register-payment-methods-edit'"
          >
            {{ $t('global.actions.edit') }}
          </BaseText>
        </Flex>
        <Flex
          justify="between"
          wrap
          gap="1"
          :class="$style.paymentMethods"
        >
          <PaymentMethod
            v-for="(payment, index) in paymentMethods"
            :key="index"
            :payment="payment.toLowerCase()"
            :amount="amount"
            :prepaidCards="prepaidCards"
            :posTerminals="connectedTerminals"
            :posConnected="selectPosTerminal"
            @addPayment="addPayment"
            @openModal="openModal"
          />
        </Flex>
        <SalonizedPayOption
          v-if="$screen !== 's'"
          mt
        />
      </Flex>
    </div>
    <GiftcardModal
      v-if="selectGiftcard"
      :paymentAmount="amount"
      @submit="addPayment"
      @close="selectGiftcard = false"
    />
    <GiftyModal
      v-if="selectGifty"
      :paymentAmount="amount"
      @submit="addPayment"
      @close="selectGifty = false"
    />
    <PrepaidCardModal
      v-if="selectPrepaidCard"
      :prepaidCards="prepaidCards"
      @submit="addPayment"
      @close="selectPrepaidCard = false"
    />
    <PosTerminalModal
      v-if="selectPosTerminal && locationTerminals.length"
      :amount="amount"
      :posTerminals="locationTerminals"
      @close="selectPosTerminal = false"
    />
    <OpenDrawerModal
      v-if="showOpenDrawerModal"
      @close="showOpenDrawerModal = false"
      @success="openModal('pos')"
    />
    <EditPaymentMethodsModal
      v-if="user?.admin && showEditPaymentMethods"
      @submit="refetchPaymentMethods"
      @close="showEditPaymentMethods = false"
    />
  </div>
</template>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import OpenDrawerModal from '@/modules/register/modals/open-drawer/index.vue';
import { useRegisterOrderStore } from '@/modules/register/stores/order';
import { useRegisterStore } from '@/modules/register/stores/register';
import { useAdyenStore } from '@/stores/adyen';
import { useRegisterDrawerStore } from '@/stores/register-drawer';
import { useUserStore } from '@/stores/user';
import { PrepaidCardStatus } from '@/types';
import EditPaymentMethodsModal from './EditPaymentMethodsModal.vue';
import GiftcardModal from './GiftcardModal.vue';
import GiftyModal from './GiftyModal.vue';
import { GET_PAYMENT_METHODS, GET_PREPAID_CARDS } from './graphql';
import PaymentMethod from './PaymentMethod.vue';
import PrepaidCardModal from './PrepaidCardModal.vue';
import PosTerminalModal from './pos/index.vue';
import SalonizedPayOption from './SalonizedPayOption.vue';

const { user } = useUserStore();
const { addTransaction, order, removeTransaction } = useRegisterOrderStore();
const { outstandingAmount, deletedTransactions } = storeToRefs(
  useRegisterOrderStore(),
);

const { result: prepaidCardsResult } = useQuery(
  GET_PREPAID_CARDS,
  () => ({
    customerId: order.customerId,
    filterScope: 'USABLE',
  }),
  () => ({ enabled: !!order.customerId }),
);

const prepaidCards = computed(() => {
  const deletedPrepaidCards = deletedTransactions.value
    .filter((transaction) => transaction.type === 'PREPAID_CARD')
    .map((transaction) => transaction.prepaidCard)
    .filter((card) => card !== undefined);
  const availablePrepaidCards = (
    prepaidCardsResult.value?.prepaidCards || []
  ).filter(
    (card) =>
      !deletedPrepaidCards.find((deletedCard) => deletedCard?.id === card.id),
  );
  return [...deletedPrepaidCards, ...availablePrepaidCards].filter(
    (card) => card.remaining > 0 && card.status !== PrepaidCardStatus.Credited,
  );
});

const {
  result: paymentMethodsResult,
  loading: fetchingPaymentMethods,
  refetch: refetchPaymentMethods,
} = useQuery(GET_PAYMENT_METHODS, () => ({
  register: true,
  locationId: order.locationId,
}));
const paymentMethods = computed(
  () => paymentMethodsResult.value?.paymentMethods || [],
);

watch(paymentMethodsResult, (result) => {
  if (!result || result.paymentMethods?.includes('GIFTY')) return;

  order.transactions
    .filter((transaction) => !transaction.id && transaction.type === 'GIFTY')
    .forEach(removeTransaction);
});

const amount = ref(outstandingAmount.value);
const selectPrepaidCard = ref(false);
const selectGiftcard = ref(false);
const selectGifty = ref(false);
const selectPosTerminal = ref(false);
const showOpenDrawerModal = ref(false);
const showEditPaymentMethods = ref(false);

const { terminals: posTerminals } = storeToRefs(useAdyenStore());
const connectedTerminals = computed(() =>
  posTerminals.value.filter((terminal: any) => terminal.status === 'CONNECTED'),
);
const locationTerminals = computed(() =>
  connectedTerminals.value.filter(
    (terminal) => terminal.location?.id === order.locationId,
  ),
);

const addPayment = (payment: {
  type: string;
  amount: number;
  id?: string;
  name?: string;
  card?: any;
  giftyCode?: string;
}) => {
  addTransaction({
    type: payment.type.toUpperCase(),
    amount: payment.amount,
    ...(payment.type === 'giftcard'
      ? { giftcardId: payment.id, name: payment.name }
      : null),
    ...(payment.type === 'gifty'
      ? { giftyCode: payment.giftyCode, name: payment.name }
      : null),
    ...(payment.type === 'prepaid_card'
      ? {
          prepaidCardId: payment.id,
          name: payment.name,
          prepaidCard: payment.card,
        }
      : null),
  });
  selectGiftcard.value = false;
  selectGifty.value = false;
  selectPrepaidCard.value = false;
};

const { isLoadingDrawerStatus } = storeToRefs(useRegisterDrawerStore());

const openModal = (type: string) => {
  if (isLoadingDrawerStatus.value) {
    watch(isLoadingDrawerStatus, () => openModal(type), { once: true });
    return;
  }

  const { isOpen } = useRegisterDrawerStore();
  const { cashupEnabled } = useRegisterStore();

  if (cashupEnabled && !isOpen) {
    showOpenDrawerModal.value = true;
    return;
  }

  switch (type) {
    case 'giftcard':
      selectGiftcard.value = true;
      break;
    case 'gifty':
      selectGifty.value = true;
      break;
    case 'prepaid_card':
      selectPrepaidCard.value = true;
      break;
    case 'pos':
      selectPosTerminal.value = true;
      break;
  }
};

const route = useRoute();
if (route.query.direct) {
  openModal('pos');
}

watch(outstandingAmount, () => {
  // amount should be updated when a new payment is added but also when a transaction is deleted (which happens in the store)
  amount.value = outstandingAmount.value;
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: $spacing;
}

.header {
  min-height: $spacing;
  margin: $spacing * 0.75 0 $spacing * .25 0;
}

.paymentMethods {
  margin-top: -$spacing;
}

.options {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.loading {
  display: flex;
  justify-content: center;
  padding: $spacing;
}
</style>
